// The ready-state «Ваш прогресс» page: days-in-service hero + metric grid.

import {
  IconCalendar,
  IconCircleCheck,
  IconClock,
  IconMessage,
  IconUser,
  type TablerIcon,
} from '@tabler/icons-react'
import type { InsightsProgress } from '../../../types/api'
import styles from './Progress.module.css'

const METRIC_ICON = {
  sessions: IconUser,
  dialog: IconClock,
  messages: IconMessage,
  practices: IconCircleCheck,
} as const

function pluralize(count: number, one: string, few: string, many: string) {
  const mod10 = count % 10
  const mod100 = count % 100
  if (mod10 === 1 && mod100 !== 11) return one
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few
  return many
}

export default function ProgressPage({ data }: { data: InsightsProgress }) {
  const metrics: Array<{ id: keyof typeof METRIC_ICON; value: string; unit?: string; label: string }> = [
    { id: 'sessions', value: String(data.sessions), label: 'Сессии' },
    { id: 'dialog', value: String(data.minutes), unit: 'мин', label: 'Время в диалоге' },
    { id: 'messages', value: data.messages == null ? '—' : String(data.messages), label: 'Сообщения' },
    { id: 'practices', value: String(data.practices_completed), label: 'Практик выполнено' },
  ]
  const daysLabel = `${pluralize(data.days_in_service, 'день', 'дня', 'дней')} в сервисе`

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>Ваш прогресс</h1>
      <p className={styles.lead}>Всё важное о вашем пути — в одном месте.</p>

      <section className={styles.hero} aria-label={`${data.days_in_service} ${daysLabel}`}>
        <div className={styles.heroMain}>
          <span className={styles.heroIcon} aria-hidden="true">
            <IconCalendar size={26} stroke={1.75} />
          </span>
          <div>
            <p className={styles.heroValue}>{data.days_in_service}</p>
            <p className={styles.heroLabel}>{daysLabel}</p>
          </div>
        </div>
        <span className={styles.orb} aria-hidden="true" />
        <span className={`${styles.orb} ${styles.orbSmall}`} aria-hidden="true" />
      </section>

      <ul className={styles.grid}>
        {metrics.map((metric) => {
          const Icon: TablerIcon = METRIC_ICON[metric.id]
          return (
            <li key={metric.id} className={styles.card}>
              <span className={styles.cardIcon} aria-hidden="true">
                <Icon size={24} stroke={1.75} />
              </span>
              <div className={styles.cardText}>
                <p className={styles.cardValue}>
                  {metric.value}
                  {metric.unit && <span className={styles.cardUnit}> {metric.unit}</span>}
                </p>
                <p className={styles.cardLabel}>{metric.label}</p>
              </div>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
